import { useEffect, useRef } from 'react';
import type { Map as MlMap } from 'maplibre-gl';
import type { ClientMessage, PublicState } from '../../shared/protocol';
import { LANDMARK_TAGS } from '../../shared/landmarkTags';
import { harvestFeatures, syntheticFeatures } from '../map/mapFeatures';

/**
 * v24 BOLT-118 — harvest the landmark features and upload them, ONCE per map.
 *
 * FIELD BUG (Jake, 2026-07-29): every "Nearest" card was dealt DISABLED on a map
 * that plainly has parks, food and water. Three causes, all fixed here:
 *
 *  1. It lived in `Lobby`, which UNMOUNTS the moment the round starts. Practice
 *     auto-starts, so the lobby exists for about a second and the retry timer
 *     died with it. It lives at APP level now, where it survives every phase
 *     change (the same place iOS puts it: `PlayModel`, not a screen).
 *  2. It fired once, immediately, and marked itself done even when the harvest
 *     came back EMPTY because tiles had not loaded. An empty harvest is
 *     indistinguishable from "this neighbourhood has no parks", and acting on
 *     that guess disables a good card for the whole game. It retries per TAG
 *     until each yields, then stops.
 *  3. It only ran in `phase === 'lobby'`. The server accepts the upload during
 *     headstart and active play too, which is exactly when a late harvest can
 *     still rescue a card.
 */
const RETRY_MS = 1500;
const MAX_ATTEMPTS = 20;
const FIRST_DELAY_MS = 400;

export function useMapFeatureUpload(state: PublicState | null, send: (m: ClientMessage) => void) {
  const uploadedTags = useRef(new Set<string>());
  const mapKey = useRef<string | null>(null);

  const boundary = state?.boundary ?? null;
  const ready = (state?.protocolVersion ?? 0) >= 10 && !!boundary;

  useEffect(() => {
    if (!ready || !boundary) return;
    // A redraw is a new map: start the harvest over.
    const key = JSON.stringify(boundary.geometry.coordinates?.[0]?.[0] ?? '');
    if (mapKey.current !== key) {
      mapKey.current = key;
      uploadedTags.current = new Set();
    }
    if (uploadedTags.current.size >= LANDMARK_TAGS.length) return;

    let attempts = 0;
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      if (uploadedTags.current.size >= LANDMARK_TAGS.length || attempts++ >= MAX_ATTEMPTS) return;
      const map = (window as unknown as { __boltMap?: MlMap }).__boltMap;
      for (const tag of LANDMARK_TAGS) {
        if (uploadedTags.current.has(tag)) continue;
        let features: ReturnType<typeof harvestFeatures> = [];
        try {
          features = map ? harvestFeatures(map, tag, boundary) : syntheticFeatures(tag, boundary);
        } catch {
          features = [];
        }
        // Empty means "not loaded yet" OR "none here". Both retry; the attempt
        // cap decides when we accept that the map really has none.
        if (!features.length) continue;
        uploadedTags.current.add(tag);
        send({ t: 'map_features', tag, features });
      }
      if (uploadedTags.current.size < LANDMARK_TAGS.length) timer = setTimeout(tick, RETRY_MS);
    };
    timer = setTimeout(tick, FIRST_DELAY_MS);
    return () => clearTimeout(timer);
  }, [ready, boundary, send]);
}
